import type { FC, JSX } from "react";
import { NavLink } from "react-router-dom";
import { NavItem } from "../types";
import { useAuth } from "../context/AuthContext";

interface SidebarProps {
    brandIcon: JSX.Element;
    brandName: string;
    navItems: NavItem[];
    managementItems?: NavItem[];
    sidebarColor: 'sky' | 'amber';
    onLinkClick?: () => void;
}

const Sidebar: FC<SidebarProps> = ({ brandIcon, brandName, navItems, managementItems, sidebarColor, onLinkClick }) => {

    // Mappa colori aggiornata con gradienti e ombre
    const colorMap = {
        sky: {
            activeClass: 'bg-gradient-to-r from-sky-600 to-indigo-600 text-white shadow-lg shadow-sky-500/20 border-l-0',
            hoverClass: 'hover:bg-slate-800 hover:text-white',
            textClass: 'text-slate-400',
            iconActive: 'text-white',
            iconInactive: 'text-slate-500 group-hover:text-sky-400'
        },
        amber: {
            activeClass: 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/20 border-l-0',
            hoverClass: 'hover:bg-slate-800 hover:text-white',
            textClass: 'text-slate-400',
            iconActive: 'text-white',
            iconInactive: 'text-slate-500 group-hover:text-amber-400'
        },
    };

    const theme = colorMap[sidebarColor];
    const { user, logout } = useAuth();

    // Componente helper per i link
    const SidebarLink = ({ item }: { item: NavItem }) => (
        <NavLink
            to={item.path}
            onClick={onLinkClick}
            className={({ isActive }) =>
                `group flex items-center p-3 mx-2 rounded-xl transition-all duration-300 ease-in-out mb-1 ${
                    isActive
                        ? `${theme.activeClass} font-medium`
                        : `${theme.textClass} ${theme.hoverClass}`
                }`
            }
        >
            {({ isActive }) => (
                <>
                    <div className={`mr-3 transition-colors duration-300 ${isActive ? theme.iconActive : theme.iconInactive}`}>
                        {item.icon}
                    </div>
                    <span className="tracking-wide text-sm">{item.label}</span>
                    {item.badge && (
                        <span className="animate-badge-breathe ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-md bg-amber-500 text-white leading-none">
                            {item.badge}
                        </span>
                    )}
                    {isActive && (
                        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/50 shadow-sm"></div>
                    )}
                </>
            )}
        </NavLink>
    );

    return (
        <aside className="w-64 bg-slate-900 border-r border-slate-800 flex-shrink-0 flex flex-col justify-between h-full z-20 shadow-2xl">
            <div className="flex flex-col h-full">
                {/* Header Logo */}
                <div className="p-6 pb-2">
                    <div className="flex items-center justify-center mb-8 mt-2">
                        <div className="w-full max-w-[180px] p-2 bg-slate-800/30 rounded-xl border border-slate-800/50 shadow-inner">
                            <img
                                src="/logo2.webp"
                                alt={brandName}
                                className="w-full h-auto object-contain rounded-lg opacity-90 hover:opacity-100 transition-opacity"
                            />
                        </div>
                    </div>
                </div>

                {/* Navigation Scrollable Area */}
                <nav className="flex-1 overflow-y-auto px-2 space-y-1 custom-scrollbar">
                    <div className="px-4 mb-2 mt-2">
                        <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">Menu Principale</p>
                    </div>
                    <ul className="space-y-1 mb-8">
                        {navItems.map(item => (
                            <li key={item.id}>
                                <SidebarLink item={item} />
                            </li>
                        ))}
                    </ul>

                    {managementItems && managementItems.length > 0 && (
                        <>
                            <div className="px-4 mb-2 mt-6">
                                <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">Amministrazione</p>
                            </div>
                            <ul className="space-y-1 pb-4">
                                {managementItems.map(item => (
                                    <li key={item.id}>
                                        <SidebarLink item={item} />
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </nav>
            </div>

            {/* Footer Profile */}
            <div className="p-4 border-t border-slate-800 bg-slate-900">
                <div className="bg-slate-800/50 rounded-2xl p-3 border border-slate-700/50 flex items-center justify-between group hover:border-slate-600 transition-colors">
                    <NavLink
                        to={user?.client ? "/client/profile" : "/admin/profile"}
                        onClick={onLinkClick}
                        className="flex items-center flex-grow min-w-0 mr-2 focus:outline-none"
                    >
                        <div className="relative flex-shrink-0">
                            <img
                                src={`https://placehold.co/40x40/64748b/ffffff?text=${user?.name?.charAt(0).toUpperCase() || 'U'}`}
                                alt={user?.name || "Avatar"}
                                className="rounded-xl w-10 h-10 border border-slate-600 shadow-sm"
                            />
                            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-500 border-2 border-slate-800 rounded-full"></div>
                        </div>
                        <div className="ml-3 overflow-hidden">
                            <p className="text-white font-semibold text-sm truncate group-hover:text-sky-400 transition-colors">
                                {user?.name || "Utente"}
                            </p>
                            <p className="text-slate-500 text-xs truncate capitalize">
                                {user?.role ? user.role.toLowerCase().replace('_', ' ') : "Guest"}
                            </p>
                        </div>
                    </NavLink>

                    <button
                        onClick={logout}
                        className="p-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all focus:outline-none active:scale-95"
                        aria-label="Esci"
                        title="Logout"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                            <polyline points="16 17 21 12 16 7"/>
                            <line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;